import { useEffect, useState } from "react";
import { formatDateTime } from "../lib/dateFormat";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { useAuth, canAtLeast } from "../lib/auth";
import { Plus, LayoutDashboard, Trash2 } from "lucide-react";

export default function DashboardsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const clientView = new URLSearchParams(location.search).get("view") === "client";
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientError, setClientError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/dashboards").then((r) => setItems(r.data.dashboards || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Client Dashboards: each client login and the dashboard assigned to it.
  useEffect(() => {
    if (!clientView) return;
    setClientError("");
    api.get("/access-management/client-users")
      .then((r) => setClients(r.data.users || []))
      .catch((e) => setClientError(e?.response?.data?.detail || e.message || "Client users could not be loaded."));
  }, [clientView]);

  const del = async (id) => {
    if (!confirm("Delete this dashboard?")) return;
    try {
      await api.delete(`/dashboards/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>{clientView ? "Client Dashboards" : "Dashboards"}</h1>
            <p>{clientView
              ? "Dashboards assigned to client logins. Each client sees only its own dashboard and data."
              : "Saved analytics dashboards for this workspace. Create, edit, and manage analytics dashboards from here."}</p>
          </div>

          {canAtLeast(user, "analyst") && (
            <button className="erp-primary-btn" onClick={() => nav("/builder/new")} data-testid="dashboards-new">
              <Plus size={15} strokeWidth={1.8} />
              New dashboard
            </button>
          )}
        </div>

        <div className="erp-card">
          {clientView ? (
            <ClientDashboardsTable clients={clients} dashboards={items} error={clientError} loading={loading} />
          ) : loading ? (
            <div className="text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
              Loading…
            </div>
          ) : (
            <table className="tbl" data-testid="dashboards-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tags</th>
                  <th>Type</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {items.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link
                        to={`/dashboards/${d.id}`}
                        className="font-medium"
                        style={{ color: "var(--c-accent)" }}
                        data-testid={`dash-row-${d.id}`}
                      >
                        <LayoutDashboard size={14} strokeWidth={1.7} className="inline mr-1.5" />
                        {d.name}
                      </Link>
                      <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                        {d.description || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="flex gap-1 flex-wrap">
                        {(d.tags || []).map((t) => <span className="pill" key={t}>{t}</span>)}
                      </div>
                    </td>

                    <td>
                      {d.is_template ? <span className="pill info">Template</span> : <span className="pill">Custom</span>}
                    </td>

                    <td className="numeric text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
                      {formatDateTime(d.updated_at)}
                    </td>

                    <td className="text-right">
                      {canAtLeast(user, "analyst") && (
                        <button className="btn btn-ghost" onClick={() => nav(`/builder/${d.id}`)} data-testid={`edit-${d.id}`}>
                          Edit
                        </button>
                      )}

                      {canAtLeast(user, "workspace_admin") && !d.is_template && (
                        <button className="btn btn-ghost" onClick={() => del(d.id)} data-testid={`del-${d.id}`}>
                          <Trash2 size={13} strokeWidth={1.7} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>
                      No dashboards yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

function ClientDashboardsTable({ clients, dashboards, error, loading }) {
  if (error) return <div className="text-[12.5px]" style={{ color: "#991b1b" }}>{error}</div>;
  if (loading) return <div className="text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>Loading…</div>;
  const byId = Object.fromEntries(dashboards.map((d) => [d.id, d]));
  return (
    <table className="tbl" data-testid="client-dashboards-table">
      <thead>
        <tr>
          <th>Client</th>
          <th>Login</th>
          <th>Assigned Dashboard</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {clients.map((c) => {
          const dashId = c.assigned_dashboard_id || (c.allowed_dashboard_ids || [])[0];
          const dash = dashId ? byId[dashId] : null;
          return (
            <tr key={c.id}>
              <td className="font-medium">{c.name || "—"}</td>
              <td className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>{c.email}</td>
              <td>
                {dash ? (
                  <Link to={`/dashboards/${dash.id}`} style={{ color: "var(--c-accent)" }}>
                    <LayoutDashboard size={14} strokeWidth={1.7} className="inline mr-1.5" />
                    {dash.name}
                  </Link>
                ) : (
                  <span className="pill">{dashId ? "Dashboard not created yet" : "No dashboard assigned"}</span>
                )}
              </td>
              <td>{c.is_active === false ? <span className="pill">Inactive</span> : <span className="pill info">Active</span>}</td>
            </tr>
          );
        })}
        {clients.length === 0 && (
          <tr>
            <td colSpan={4} className="text-center py-6" style={{ color: "var(--c-text-muted)" }}>
              No client logins yet.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
